import React, { useState, useEffect } from 'react';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { useAuth } from '../hooks';
import { API_BASE_URL } from '../constants';
import documentService from '../services/documentService';
import '../styles/DocumentLibrary.css';

const DocumentLibrary = () => {
  const { currentUser, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFramework, setSelectedFramework] = useState('ALL');
  const [selectedType, setSelectedType] = useState('ALL');
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const LIMIT = 20;

  const frameworks = [
    { id: 'ALL', label: 'All Frameworks' },
    { id: 'MICA', label: 'MiCA' },
    { id: 'DORA', label: 'DORA' },
    { id: 'GDPR', label: 'GDPR' },
    { id: 'AMLR', label: 'AML/CFT' },
    { id: 'PSD', label: 'PSD2/PSD3' },
    { id: 'MIFID', label: 'MiFID II' }
  ];

  const documentTypes = [
    { id: 'ALL', label: 'All Types' },
    { id: 'REGULATION', label: 'Regulations' },
    { id: 'GUIDELINE', label: 'Guidelines' },
    { id: 'OPINION', label: 'Opinions' },
    { id: 'REPORT', label: 'Reports' },
    { id: 'Q&A', label: 'Q&A' }
  ];

  const searchLibrary = async (resetOffset = false) => {
    try {
      setLoading(true);
      const newOffset = resetOffset ? 0 : offset;

      const params = new URLSearchParams({
        limit: LIMIT.toString(),
        offset: newOffset.toString()
      });

      if (searchQuery.trim()) {
        params.append('query', searchQuery.trim());
      }
      if (selectedFramework !== 'ALL') {
        params.append('framework', selectedFramework);
      }
      if (selectedType !== 'ALL') {
        params.append('type', selectedType);
      }

      const response = await fetch(
        `${API_BASE_URL}/library/search?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error('Failed to search library');
      }

      const data = await response.json();
      setDocuments(data.documents || []);
      setTotal(data.total || 0);
      setHasMore(data.hasMore || false);
      if (resetOffset) setOffset(0);

    } catch (error) {
      console.error('Library search error:', error);
      setDocuments([]);
      setTotal(0);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    searchLibrary(true);
  }, [selectedFramework, selectedType]);

  const handleSearch = (e) => {
    e.preventDefault();
    searchLibrary(true);
  };

  const handleLoadMore = () => {
    setOffset(prev => prev + LIMIT);
    searchLibrary(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const parseDocumentTitle = (doc) => {
    // If there's already a descriptive title, use it
    if (doc.title && doc.title.length > 10 && !doc.title.endsWith('.zip') && !doc.title.endsWith('.xml')) {
      return doc.title;
    }

    // Parse filename for better display
    const filename = doc.fileName || doc.title || '';

    // Document type mappings
    const typeMap = {
      'DLTINS': 'Data on Listed Instruments',
      'FULINS': 'Full Instrument Data',
      'FIRDS': 'Financial Instruments Reference Data',
      'REPRICES': 'Reference Price Data',
      'PRICES': 'Pricing Data',
      'TRADING': 'Trading Venue Data',
      'ESMA': 'ESMA Document',
      'EBA': 'EBA Document',
      'EIOPA': 'EIOPA Document'
    };

    // Extract document type from filename
    for (const [key, value] of Object.entries(typeMap)) {
      if (filename.toUpperCase().includes(key)) {
        // Extract date if present (format: YYYYMMDD)
        const dateMatch = filename.match(/(\d{8})/);
        // Extract part number (e.g., "01of02")
        const partMatch = filename.match(/(\d+of\d+)/);

        let title = value;
        if (dateMatch) {
          const dateStr = dateMatch[1];
          const year = dateStr.substring(0, 4);
          const month = dateStr.substring(4, 6);
          const day = dateStr.substring(6, 8);
          title += ` - ${day}/${month}/${year}`;
        }
        if (partMatch) {
          title += ` (Part ${partMatch[1]})`;
        }

        return title;
      }
    }

    // If no match, clean up the filename
    return filename.replace(/\.zip$|\.xml$|\.pdf$/i, '').replace(/_/g, ' ');
  };

  const getFileFormat = (doc) => {
    const filename = doc.fileName || doc.title || '';
    if (filename.endsWith('.zip')) return 'ZIP Archive';
    if (filename.endsWith('.xml')) return 'XML Data';
    if (filename.endsWith('.pdf')) return 'PDF Document';
    return doc.docType || 'Document';
  };

  const handleDownload = async (doc, event) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      await documentService.downloadPDF(doc);
    } catch (error) {
      alert(error.message || 'Failed to download document');
    }
  };

  return (
    <>
      <Header
        currentUser={currentUser}
        showUserMenu={showUserMenu}
        setShowUserMenu={setShowUserMenu}
        onLogout={logout}
      />

      <div className="app">
        <Sidebar />

        <div className="main-content">
          <div className="content-area">

            {/* Hero Section */}
            <div className="library-hero">
              <div className="library-hero-content">
                <div className="library-hero-label">DOCUMENT LIBRARY</div>
                <h1 className="library-hero-title">Regulatory Document Search</h1>
                <p className="library-hero-description">
                  Search across 7,579 regulatory documents from ESMA, EBA, EIOPA, EDPB, and ENISA.
                  Filter by framework, document type, and publication date.
                </p>
              </div>
            </div>

            {/* Search Section */}
            <div className="library-search-section">
              <form onSubmit={handleSearch} className="library-search-form">
                <div className="search-input-wrapper">
                  <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="11" cy="11" r="8"/>
                    <path d="m21 21-4.35-4.35"/>
                  </svg>
                  <input
                    type="text"
                    className="library-search-input"
                    placeholder="Search documents by title, description, or content..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <button type="submit" className="search-button">
                    Search
                  </button>
                </div>
              </form>

              {/* Filters */}
              <div className="library-filters">
                <div className="filter-group">
                  <label className="filter-label">Framework</label>
                  <select
                    className="filter-select"
                    value={selectedFramework}
                    onChange={(e) => setSelectedFramework(e.target.value)}
                  >
                    {frameworks.map(fw => (
                      <option key={fw.id} value={fw.id}>{fw.label}</option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label className="filter-label">Document Type</label>
                  <select
                    className="filter-select"
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                  >
                    {documentTypes.map(type => (
                      <option key={type.id} value={type.id}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <div className="filter-stats">
                  <span className="stats-count">{total.toLocaleString()}</span>
                  <span className="stats-label">documents found</span>
                </div>
              </div>
            </div>

            {/* Documents Section */}
            <div className="documents-section">
              {loading && offset === 0 ? (
                <div className="loading-state">Searching documents...</div>
              ) : documents.length === 0 ? (
                <div className="empty-state">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="empty-icon">
                    <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                  </svg>
                  <p>No documents found matching your search criteria.</p>
                  <p className="empty-hint">Try adjusting your filters or search query.</p>
                </div>
              ) : (
                <>
                  <div className="documents-grid">
                    {documents.map((doc, index) => (
                      <div key={doc.id || index} className="document-card">
                        <div className="document-header">
                          <div className="document-type-badge">
                            {getFileFormat(doc)}
                          </div>
                          <div className="document-date">
                            {formatDate(doc.publicationDate || doc.pubDate)}
                          </div>
                        </div>

                        <h3 className="document-title">{parseDocumentTitle(doc)}</h3>

                        {doc.fileName && (
                          <div className="document-filename">
                            <svg className="filename-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            {doc.fileName}
                          </div>
                        )}

                        {doc.description && (
                          <p className="document-description">
                            {doc.description.length > 150
                              ? `${doc.description.substring(0, 150)}...`
                              : doc.description}
                          </p>
                        )}

                        <div className="document-meta">
                          {doc.authority && (
                            <div className="meta-item">
                              <span className="meta-label">Authority:</span>
                              <span className="meta-value">{doc.authority}</span>
                            </div>
                          )}
                          {doc.source && (
                            <div className="meta-item">
                              <span className="meta-label">Source:</span>
                              <span className="meta-value">{doc.source.toUpperCase()}</span>
                            </div>
                          )}
                        </div>

                        <div className="document-footer">
                          {documentService.isDownloadable(doc) && (
                            <button
                              onClick={(e) => handleDownload(doc, e)}
                              className="document-button"
                              title={documentService.getDownloadLabel(doc)}
                            >
                              <svg className="button-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              {documentService.getDownloadLabel(doc)}
                            </button>
                          )}
                          {doc.detailUrl && (
                            <a
                              href={doc.detailUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="document-link"
                            >
                              View Details
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {hasMore && (
                    <div className="load-more-section">
                      <button
                        className="load-more-button"
                        onClick={handleLoadMore}
                        disabled={loading}
                      >
                        {loading ? 'Loading...' : 'Load More Documents'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default DocumentLibrary;
